/* eslint-disable camelcase */
import axios from 'axios';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import Button from '../../components/Button';
import '../SignIn/SignIn.scss';

export default function SetPassword({ onServerError = () => {} }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const tr = (key) => t(`common.pages.set_password.${key}`);

  const validationSchema = Yup.object({
    password: Yup.string()
      .required(tr('errors.new_password_required'))
      .min(8, tr('errors.min_length')),
    passwordConfirmation: Yup.string()
      .required(tr('errors.confirm_password_required'))
      .oneOf([Yup.ref('password'), null], tr('errors.mismatch')),
  });

  const formik = useFormik({
    initialValues: { password: '', passwordConfirmation: '' },
    validationSchema,
    onSubmit: (values) => {
      setLoading(true);
      onServerError(false);

      axios.post('/api/v1/influencers/users/set_initial_password', {
        user: {
          password: values.password,
          password_confirmation: values.passwordConfirmation,
        },
      })
        .then(({ data }) => {
          if (data && data.success) {
            window.location.href = '/manager/home';
            return;
          }
          handleFailure(get(data, 'errors'));
          setLoading(false);
        })
        .catch((error) => {
          const status = get(error, 'response.status');
          if (status === 422) {
            handleFailure(get(error, 'response.data.errors'));
          } else {
            onServerError(true);
          }
          setLoading(false);
        });
    },
  });

  const handleFailure = (errors) => {
    const passwordError = get(errors, 'password.0');
    const confirmationError = get(errors, 'password_confirmation.0');
    if (!passwordError && !confirmationError) {
      onServerError(true);
      return;
    }
    if (passwordError) {
      formik.setFieldError('password', passwordError);
    }
    if (confirmationError) {
      formik.setFieldError('passwordConfirmation', confirmationError);
    }
  };

  const renderField = ({ name, label, placeholder, show, toggle }) => (
    <Form.Group className='mb-3'>
      <Form.Label>{label}</Form.Label>
      <InputGroup>
        <Form.Control
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          id={name}
          name={name}
          onChange={formik.handleChange}
          value={formik.values[name]}
          autoComplete='new-password' />
        <Button onClick={toggle}>
          <i className={classNames('fal', show ? 'fa-eye-slash' : 'fa-eye')} />
        </Button>
      </InputGroup>
      <Form.Text className='text-muted danger-text'>
        {formik.touched[name] && formik.errors[name]}
      </Form.Text>
    </Form.Group>
  );

  return <Form onSubmit={formik.handleSubmit}>
    <Card>
      <Card.Header>
        <h3 className='mb-sm-4'>{tr('title')}</h3>
      </Card.Header>
      <Card.Body className='mb-4-5'>
        <p className='text-muted'>{tr('hint')}</p>
        {renderField({
          name: 'password',
          label: tr('new_password'),
          placeholder: tr('new_password_placeholder'),
          show: showPassword,
          toggle: () => setShowPassword(!showPassword),
        })}
        {renderField({
          name: 'passwordConfirmation',
          label: tr('confirm_password'),
          placeholder: tr('confirm_password_placeholder'),
          show: showConfirmation,
          toggle: () => setShowConfirmation(!showConfirmation),
        })}
      </Card.Body>
      <hr />
      <Card.Footer>
        <div className='flex flex-column align-items-end'>
          <Button
            id='set-password-button'
            variant='primary'
            type='submit'
            disabled={loading}>
            {loading ? <Spinner animation='border' size='sm' /> : tr('submit')}
          </Button>
        </div>
      </Card.Footer>
    </Card>
  </Form>;
}

SetPassword.propTypes = {
  onServerError: PropTypes.func,
};
